<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props" style="display: inline;">
        <slot name="default"></slot>
      </div>
    </template>

    <v-card>
      <v-card-title class="d-flex justify-space-between">
        {{$t('routes.pod.requestFilesLink')}}
        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-tabs v-model="tab" align-tabs="center" show-arrows>
        <v-tab>{{$t('menus.menuUploadLink.uploadLinkInformation')}}</v-tab>
        <v-tab>{{$t('menus.menuUploadLink.emailInvitations')}}</v-tab>
      </v-tabs>

      <v-card-text class="scrollable pt-4">
        <v-form ref="form" v-on:submit.prevent="submitForm" :disabled="false" autocomplete="off">
          <v-window v-model="tab" style="background-color: unset !important;">
            <v-window-item class="pt-4">
              <div class="d-flex">
                <div class="flex-grow-1">
                  {{ $t('menus.menuUploadLink.password') }}:
                  <v-text-field
                    type="text"
                    :placeholder="$t('menus.menuUploadLink.password')"
                    :aria-label="$t('menus.menuUploadLink.password')"
                    v-model="password"
                    v-bind:rules="passwordRules"
                    :error-messages="passwordErrorMessage"
                    @update:model-value="$refs.form.validate()"
                    variant="solo-filled"
                    required block flat
                    data-cy="tools-uploadLink-password"
                  >
                    <template v-slot:append-inner>
                      <v-tooltip
                        v-if="canAccessClipboard && password && password.length > 0"
                        location="bottom"
                        :aria-label="$t('menus.menuUploadLink.copyPassword')"
                      >
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="props"
                            v-on:click="copyPasswordToClipboard"
                            :aria-label="$t('menus.menuUploadLink.copyPassword')"
                            variant="text"
                            icon
                            width="36"
                            height="36"
                          >
                            <v-icon>mdi-content-copy</v-icon>
                          </v-btn>
                        </template>

                        <span>{{ $t('menus.menuUploadLink.copyPassword') }}</span>
                      </v-tooltip>

                      <v-tooltip location="bottom" :z-index="1200" :aria-label="$t('menus.menuUploadLink.generatePassword')">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="props"
                            v-on:click="generatePassword"
                            :aria-label="$t('menus.menuUploadLink.generatePassword')"
                            variant="text"
                            icon
                            width="36"
                            height="36"
                          >
                            <v-icon>mdi-lock-reset</v-icon>
                          </v-btn>
                        </template>

                        <span>{{ $t('menus.menuUploadLink.generatePassword') }}</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </div>
              </div>

              <div>
                {{$t('menus.menuUploadLink.expiryDate')}}:
                <v-menu
                  v-model="datepickerMenu"
                  v-bind:close-on-content-click="false"
                  v-bind:offset="40"
                  transition="scale-transition"
                  min-width="auto"
                >
                  <template v-slot:activator="{ props }">
                    <v-text-field
                      v-model="expiryDate"
                      :placeholder="$t('menus.menuUploadLink.expiryDate')"
                      :aria-label="$t('menus.menuUploadLink.expiryDate')"
                      v-bind:rules="expiryDateRules"
                      append-inner-icon="mdi-calendar"
                      readonly
                      required
                      block
                      variant="solo-filled"
                      flat
                      v-bind="props"
                      data-cy="tools-uploadLink-dateMenu"
                    />
                  </template>

                  <v-date-picker
                    v-model="expiryDate"
                    @update:model-value="datepickerMenu = false"
                    v-bind:min="getMinDatepicker"
                    data-cy="tools-uploadLink-datePicker"
                  />
                </v-menu>
              </div>

              <div class="d-flex">
                <div class="flex-grow-1">
                  {{$t('menus.menuUploadLink.maxFilesize')}}:
                  <v-text-field
                    v-model="maxfilesize"
                    type="text"
                    :placeholder="$t('menus.menuUploadLink.maxFilesize')"
                    :aria-label="$t('menus.menuUploadLink.maxFilesize')"
                    :rules="maxfilesizeRules"
                    :error-messages="maxfilesizeErrorMessage"
                    required
                    block
                    variant="solo-filled"
                    flat
                    data-cy="tools-uploadLink-maxFileSize"
                  />
                </div>
                <div class="ml-4 flex-grow-0">
                  &nbsp;
                  <v-select
                    v-model="maxfilesizeunit"
                    :items="availableFilesizeUnits"
                    flat
                    variant="solo-filled"
                  />
                </div>
              </div>

              <div>
                {{ $t('menus.menuUploadLink.notes') }}:
                <v-textarea
                  v-model="linkNotes"
                  :placeholder="$t('menus.menuUploadLink.notes')"
                  :aria-label="$t('menus.menuUploadLink.notes')"
                  variant="solo-filled"
                  flat
                  rows="3"
                  class="media-list-notes-textarea"
                  data-cy="tools-uploadLink-notes"
                />
              </div>

                <v-checkbox
                  v-if="isTeamMemberUser"
                  class="mt-0"
                  v-model="teamOnly"
                  density="compact"
                  :label="$t('menus.menuUploadLink.teamOnly')"
                  hide-details
                  required
                  block
                />

                <v-checkbox
                  v-model="embeddable"
                  density="compact"
                  :label="$t('menus.menuUploadLink.asEmbeddableWidget')"
                  :rules="embeddableRules"
                  :error-messages="password && embeddable ? $t('menus.menuUploadLink.cannotCreateEmbeddable') : ''"
                  required
                  block
                />

              <div class="mb-0 font-italic pa-0 rounded">
                <v-icon size="x-small">mdi-information-slab-circle</v-icon>
                {{$t('menus.menuUploadLink.destinationFolderInfo')}}: {{uploadlink.node.name}}
              </div>
            </v-window-item>

            <v-window-item class="pt-4">
              <div>
                <v-form ref="emailsForm" class="mt-4" @submit.prevent="addEmailRecipient">
                  <v-text-field
                    v-model="addEmailInput"
                    :rules="addEmailInputRules"
                    :placeholder="$t('common.emailAddress')"
                    :hint="$t('routes.pod.menuAccesslink.enterEmailAddressAndPress')"
                    variant="solo-filled"
                    class="mb-2"
                    flat
                  >
                    <template v-slot:append-inner>
                      <v-btn
                        size="small"
                        height="32"
                        density="default"
                        variant="elevated"
                        v-show="addEmailInput"
                        @click="addEmailRecipient"
                        prepend-icon="mdi-plus-circle"
                      >
                        {{$t('common.add')}}
                      </v-btn>
                    </template>
                  </v-text-field>
                </v-form>

                <div v-if="emailRecipientsList.length > 0" class="vb-invited-recipients">
                  <v-chip
                    v-for="email in emailRecipientsList"
                    :key="email"
                    @click:close="removeEmailRecipient(email)"
                    variant="flat"
                    closable
                    close-icon="mdi-close"
                  >
                    {{email}}
                  </v-chip>
                </div>

                <v-textarea
                  v-model="emailNote"
                  :placeholder="$t('menus.menuUploadLink.noteToEmail')"
                  :hint="$t('menus.menuUploadLink.noteToEmailHint')"
                  variant="solo-filled"
                  flat
                  rows="3"
                  class="mt-3"
                ></v-textarea>

                <div>
                  <v-checkbox
                    v-model="includeQrCode"
                    v-bind:label="$t('routes.pod.menuAccesslink.addQrCodeToEmail')"
                    hide-details
                  >
                  </v-checkbox>
                </div>
              </div>

              <div v-if="uploadlink?.invitations && Array.isArray(uploadlink.invitations) && uploadlink.invitations.length > 0" class="mt-4">
                <span class="font-weight-bold">{{ $t('routes.pod.menuAccesslink.invitedPeople') }}:</span> {{ uploadlink.invitations.map((i) => i.email).join(', ') }}
              </div>
            </v-window-item>
          </v-window>

          <div v-show="successMessage" class="bg-success mt-2" data-cy="------">{{successMessage}}</div>
          <div v-show="errorMessage" class="bg-error mt-2" data-cy="-----">{{errorMessage}}</div>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          @click="submitForm"
          :disabled="false"
          color="primary"
          variant="flat"
          data-cy="dialog--ok"
        >
          {{$t('common.update')}}
        </v-btn>
        <v-btn @click="dialog = false" variant="text">{{$t('common.close')}}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useNodeStore } from '@/stores/NodeStore.js';
import { useUploadLinkStore } from '@/stores/UploadLinkStore.js';
import Helper from '@/components/helper.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import { formatDate, formatDateTimeMilliseconds } from '@/components/formatters.js';
import { UTCDate } from '@date-fns/utc';
import { set } from 'date-fns/set';

export default {
  mixins: [DialogActionKeys],
  name: "UploadlinkUpdateDialog",
  props: {
    uploadlink: {
      type: Object,
      required: true,
    },
  },
  data() {
    const self = this;

    return {
      dialog: false,
      tab: 0,

      passwordErrorMessage: "",
      maxfilesizeErrorMessage: "",

      password: "",
      passwordRules: [
        function(v) {
          if (v && v.length < 8) {
            return this.$t('routes.pod.menuAccesslink.passwordMinLength');
          } else if (v && v.length > 191) {
            return this.$t('routes.pod.menuAccesslink.passwordMaxLength');
          } else if (v && !Helper.isValidPassword(v)) {
            return this.$t('menus.createUser.passwordInvalidChars');
          }

          return true;
        }.bind(this),
      ],
      selectedNodeId: null,
      selectedNode: null,
      teamOnly: false,

      datepickerMenu: false,
      expiryDateValue: new Date(),
      expiryDateRules: [
        v => !!v || this.$t('menus.menuUploadLink.expiryDateRequired')
      ],

      linkNotes: "",
      maxfilesize: null,
      maxfilesizeRules: [
        function (v) {
          if (v === "" || v === null) return true;
          let numV = parseFloat(v);

          if (isNaN(numV)) return self.$t('menus.menuUploadLink.invalidFilesize');
          if (numV <= 0) return self.$t('menus.menuUploadLink.filesizeMustBeGreater');

          if ( self.maxfilesizeunit === "GB" && numV > 500)
            return self.$t('menus.menuUploadLink.filesizeCannotBeGreater');

          if (self.maxfilesizeunit === "MB" && numV > 500000)
            return self.$t('menus.menuUploadLink.filesizeCannotBeGreater');

          return true;
        }
      ],

      maxfilesizeunit: "MB",
      availableFilesizeUnits: ["MB", "GB"],

      embeddable: false,
      embeddableRules: [
        function(v) {
          if (this.password && v) return this.$t('menus.menuUploadLink.cannotCreateEmbeddable');
          return true;
        }.bind(this)
      ],

      addEmailInput: "",
      addEmailInputRules: Helper.getEmailValidationRules(this, { required: false }),

      emailRecipientsList: [],
      emailNote: '',
      includeQrCode: false,

      errorMessage: "",
      successMessage: "",
    };
  },
  watch: {
    dialog(openDialog) {
      if (openDialog) {
        this.resetForm();
        this.resetErrorMessages();

        this.$nextTick(async () => {
          if (this.$refs.form) {
            this.$refs.form.resetValidation();
          } else {
            console.warn('Form ref not available yet');
          }
        });
      }
    }
  },
  methods: {
    resetForm() {
      this.password = this.uploadlink.password || "";
      this.linkNotes = this.uploadlink.note || "";
      this.expiryDate = formatDate(new UTCDate(this.uploadlink.expireat));

      this.maxfilesize = "";
      if (this.uploadlink.params && this.uploadlink.params.maxfilesize) {
        this.maxfilesize = this.uploadlink.params.maxfilesize / 1000000; // to MB (not Mb)
      }

      this.maxfilesizeunit = this.availableFilesizeUnits[0];
      if (
        this.uploadlink.params &&
        this.uploadlink.params.maxfilesizeunit &&
        this.availableFilesizeUnits.includes(this.uploadlink.params.maxfilesizeunit)
      ) {
        this.maxfilesizeunit = this.uploadlink.params.maxfilesizeunit;
      }

      if (this.maxfilesize && this.uploadlink.params.maxfilesizeunit === 'GB') {
        this.maxfilesize /= 1000;
      }

      if (this.uploadlink.params && this.uploadlink.params.embeddable) {
        this.embeddable = !!this.uploadlink.params.embeddable;
      } else {
        this.embeddable = false;
      }

      this.selectedNodeId = this.uploadlink.nodeid;
      this.teamOnly = this.uploadlink.teamonly;

      this.includeQrCode = false;
      this.addEmailInput = "";
      this.emailRecipientsList = [];
      this.emailNote = '';
    },
    resetErrorMessages() {
      this.errorMessage = "";
      this.successMessage = "";
      this.passwordErrorMessage = "";
      this.maxfilesizeErrorMessage = "";
    },
    async submitForm() {
      this.errorMessage = "";
      this.successMessage = "";

      const validForm = await this.$refs.form.validate();
      if (!validForm.valid) {
        return;
      }

      const nodeId = this.selectedNodeId || this.getNodeID;

      // convert to bytes
      let maxfilesize = parseFloat(this.maxfilesize) * 1000000;
      if (this.maxfilesizeunit === 'GB') {
        maxfilesize *= 1000;
      }

      const payload = {
        formData: {
          password: this.password.length > 0 ? this.password : null,
          note: this.linkNotes,
          teamonly: this.teamOnly,
          embeddable: this.embeddable ? 1 : 0,
          maxfilesize: maxfilesize,
          maxfilesizeunit: this.maxfilesizeunit,
          nodeid: nodeId,
          // Send the date in UTC
          expireat: formatDateTimeMilliseconds(set(new UTCDate(this.expiryDate), {
            hours: 23,
            minutes: 59,
            seconds: 59,
            milliseconds: 0,
          })),
        },
      };

      const uploadLinkStore = useUploadLinkStore();

      let VERB = uploadLinkStore.createUploadLink;
      const self = this;

      if (this.uploadlink) {
        payload.id = this.uploadlink.id;
        VERB = uploadLinkStore.updateUploadLink;
      }

      try {
        const uploadlink = await VERB(payload);

        if (self.emailRecipientsList.length > 0) {
          const links = await self.sendInvitations(uploadlink);
          self.updatedLink = links.find((x) => x.id === uploadlink.id);

          const uploadLinkStore = useUploadLinkStore();
          uploadLinkStore.updateUploadlinks(links);
        } else {
          self.updatedLink = uploadlink;
        }

        const uploadLinkStore = useUploadLinkStore();
        await uploadLinkStore.refreshUploadLinks(self.getNodeID);
        self.dialog = false;
      } catch (error) {
        if (error?.response?.data?.error) {
          const errorType = error.response.data.error;
          switch (errorType.code) {
            case "11016":
              self.passwordErrorMessage = self.$localizeMessage(error.response.data.error);
              break;
            case "ERR83":
              self.maxfilesizeErrorMessage = self.$localizeMessage(error.response.data.error);
              break;
            default:
              self.errorMessage = self.$localizeMessage(error.response.data.error);
              console.log(error?.response?.data); // unhandled error
          }
        } else {
          self.errorMessage = self.$t('backend.system-error');
          console.log(error?.response?.data); // unhandled error
        }
      }
    },
    generatePassword() {
      const randPassword = Helper.generatePassword(10);
      this.password = randPassword;
    },
    copyPasswordToClipboard() {
      if (this.password.length > 0) {
        navigator.clipboard.writeText(this.password);
      }
    },
    addEmailRecipient() {
      const validForm = this.$refs.emailsForm.validate();

      if (!validForm) {
        return;
      }

      if (this.emailRecipientsList.indexOf(this.addEmailInput) >= 0) {
        this.addEmailInput = "";
        return;
      }

      this.emailRecipientsList.push(this.addEmailInput);
      this.addEmailInput = "";
    },
    removeEmailRecipient(email) {
      this.emailRecipientsList = this.emailRecipientsList.filter(x => x !== email);
    },
    sendInvitations(uploadlink) {
      this.errorMessage = '';
      const self = this;

      const payload = {
        linkId: uploadlink.id,
        receiver: this.emailRecipientsList.join(','),
        note: this.emailNote,
        includeqrcode: this.includeQrCode ? 1 : 0,
      };

      const uploadLinkStore = useUploadLinkStore();
      return uploadLinkStore.inviteUploadlink(payload)
        .then(function(response) {
          self.emailRecipientsList = [];
          self.emailNote = '';

          return Array.isArray(response?.links) ? response.links : [];
        });
    },
  },
  computed: {
    expiryDate: {
      get: function () {
        return formatDate(this.expiryDateValue);
      },
      set: function (value) {
        this.expiryDateValue = value;
      }
    },
    getMinDatepicker() {
      return formatDate(new Date());
    },
    currentNode() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
    getAccessPoints() {
      const nodeStore = useNodeStore();
      return nodeStore.getAccessPoints;
    },
    getNodeID() {
      if (this.currentNode && this.currentNode.id) {
        return this.currentNode.id;
      } else if (this.getAccessPoints && this.getAccessPoints.length > 0) {
        return this.getAccessPoints[0].id;
      }

      return false;
    },
    isTeamMemberUser() {
      return this.$canControlTenantConfig() || this.$canBelongstoTeam();
    },
    canAccessClipboard() {
      // Note: this works only on localhost and HTTPS
      return window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.writeText === 'function';
    }
  }
};
</script>

<style>
.vb-invited-recipients .v-chip {
  color: #1c1c1c !important;
}
</style>
